import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { policySections, readPolicyContent } from "@1-now/booking-core";
import { Checkbox } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { getPrivacyPolicyContent } from "api/rental-agreement/rental-agreement";
import TermsandConditions from "designs/Common/CheckOutPage/TermsandConditions";
import PrivacyPolicy from "designs/Common/CheckOutPage/PrivacyPolicy";
import Cancellation from "designs/Common/CheckOutPage/Cancellation";
import BottomSheet from "./BottomSheet";
import PolicyReader from "./PolicyReader";

/** What the Pay step says when the renter presses Pay now or Reserve before ticking the consent line. */
export const CONSENT_REFUSAL = "Please accept the Rental Agreement and policies";
/** The consent line's element: the refusal scrolls it into view. */
export const CONSENT_ID = "pay-consent";

type Doc = "agreement" | "terms" | "privacy" | "cancellation";
const DOC_TITLE: Record<Doc, string> = {
  agreement: "Rental Agreement",
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
  cancellation: "Cancellation Policy",
};

/**
 * The rental agreement as the old checkout showed it (1NOW-11205): the FMS viewer's page for the booking, in a
 * frame that fills the reader's body. The note behind it is what shows until the viewer has painted.
 */
const AgreementFrame: FC<{ url: string }> = ({ url }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative h-full min-h-[420px]">
      {!loaded && <p className="absolute inset-0 grid place-items-center text-sm opacity-70">Loading the agreement…</p>}
      <iframe src={url} title="Rental Agreement" onLoad={() => setLoaded(true)} className="relative h-full w-full border-0" />
    </div>
  );
};

/**
 * The one consent line of the Pay step (1NOW-11205), in the way SIXT draws it: a round box and a single sentence
 * whose document names are inline underlined links. It replaces the three boxes of the review, so the renter
 * accepts once, for the booking that exists by now. The documents open from the company's policy content, except
 * the rental agreement: with `agreementUrl` (the booking the review made) it is that booking's real, filled-in
 * agreement, in a frame. They open in the desktop drawer (`drawer`) or the phone's pop-up, as before, and only
 * read: the box is the renter's to tick.
 */
const PolicyConsent: FC<{
  accepted: boolean;
  onChange: (next: boolean) => void;
  companyName?: string;
  /** Start loading the documents (the step that holds this is open). */
  active: boolean;
  /** The booking's filled-in rental agreement (the FMS viewer's address), when there is a booking. */
  agreementUrl?: string;
  /** Open the documents in the desktop drawer instead of the phone's pop-up. */
  drawer?: boolean;
}> = ({ accepted, onChange, companyName, active, agreementUrl, drawer }) => {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [content, setContent] = useState<Record<string, any[]> | null>(null);

  useEffect(() => {
    if (!active || content) return;
    let live = true;
    getPrivacyPolicyContent().then((r) => live && setContent(readPolicyContent(r)));
    return () => {
      live = false;
    };
  }, [active, content]);

  const sections = (kind: string) => policySections(content, kind);
  const owner = companyName ? `${companyName}'s` : "the";
  const link = (d: Doc, label: string): ReactNode => (
    <button type="button" onClick={() => setDoc(d)} className="inline font-semibold underline underline-offset-2">
      {label}
    </button>
  );
  const closeButton = (
    <button
      type="button"
      className={`flex min-h-[48px] items-center justify-center bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary text-sm text-on-button ${
        drawer ? "min-h-[44px] min-w-[120px] rounded-lg px-5 font-medium" : "w-full rounded-full px-6 py-3 font-extrabold"
      }`}
      onClick={() => setDoc(null)}
    >
      Close
    </button>
  );
  // The drawer keeps the last document while it slides out, instead of emptying first.
  const lastDoc = useRef<Doc | null>(null);
  if (doc) lastDoc.current = doc;
  const shown = doc ?? lastDoc.current;

  return (
    <>
      <div id={CONSENT_ID} className="flex items-start gap-3">
        <Checkbox
          checked={accepted}
          onChange={onChange}
          aria-label={`I've read and agree to ${owner} Rental Agreement, Terms & Conditions, Privacy Policy and Cancellation Policy.`}
          className="group mt-px size-6 shrink-0 rounded-full p-0.5 ring-1 ring-inset ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20"
        >
          <CheckIcon className={`size-5 stroke-primary-brand ${accepted ? "visible" : "invisible"}`} />
        </Checkbox>
        <p id={`${CONSENT_ID}-text`} className="min-w-0 text-sm leading-6">
          I&apos;ve read and agree to {owner} {link("agreement", "Rental Agreement")},{" "}
          {link("terms", "Terms & Conditions")}, {link("privacy", "Privacy Policy")} and{" "}
          {link("cancellation", "Cancellation Policy")}.
        </p>
      </div>

      {drawer ? (
        <BottomSheet
          drawer
          open={active && doc !== null}
          onClose={() => setDoc(null)}
          title={shown ? DOC_TITLE[shown] : ""}
          footer={<div className="flex justify-end gap-3">{closeButton}</div>}
        >
          {shown === "agreement" && agreementUrl ? (
            <AgreementFrame url={agreementUrl} />
          ) : content === null ? (
            <p className="py-6 text-sm opacity-70">Loading…</p>
          ) : (
            shown && (
              <PolicyReader
                sections={sections(
                  shown === "privacy" ? "privacy_policy" : shown === "cancellation" ? "cancellation_policy" : "terms_condition",
                )}
                intro={
                  shown === "agreement"
                    ? "Your rental agreement is filled in with your booking details once you book. These are the terms it follows."
                    : shown === "terms"
                      ? `The following booking Terms and Conditions apply to bookings placed directly on ${
                          companyName ? `the ${companyName}` : "this"
                        } website.`
                      : undefined
                }
              />
            )
          )}
        </BottomSheet>
      ) : (
        <BottomSheet
          open={active && doc !== null}
          onClose={() => setDoc(null)}
          title={doc ? DOC_TITLE[doc] : ""}
          tall
          footer={closeButton}
        >
          {doc === "agreement" && agreementUrl ? (
            <AgreementFrame url={agreementUrl} />
          ) : content === null ? (
            <p className="py-6 text-sm opacity-70">Loading…</p>
          ) : doc === "privacy" ? (
            <PrivacyPolicy terms={sections("privacy_policy")} />
          ) : doc === "cancellation" ? (
            <Cancellation terms={sections("cancellation_policy")} />
          ) : (
            <>
              {doc === "agreement" && (
                <p className="mt-2 text-sm opacity-80">
                  Your rental agreement is filled in with your booking details once you book. These are the terms it
                  follows.
                </p>
              )}
              <TermsandConditions
                data={{ company: { name: companyName }, content: { terms_condition: sections("terms_condition") } }}
              />
            </>
          )}
        </BottomSheet>
      )}
    </>
  );
};

export default PolicyConsent;
